class MultipleQuickBuy extends HTMLElement {
	constructor() {
		super();

		// Cache elements with null checks
		this.cartDrawer = document.querySelector('drawer-component[data-type="cart"]');
		this.drawer = document.querySelector('drawer-component[data-type="drawer"]');

		// Cache frequently accessed elements
		this.cachedElements = {};

		this.classes = {
			isVisible: 'is-visible',
			summaryAddToCartDisabled: 'button--disabled',
			summaryHidden: 'hidden',
			summaryAddToCartLoading: 'button--loading',
			singleAddToCartLoading: 'button--loading',
		};

		this.selectors = {
			product: '.multiple-quick-buy__product',
			summary: '.multiple-quick-buy__summary',
			summaryQuantity: '.multiple-quick-buy__summary-quantity',
			summaryTotal: '.multiple-quick-buy__summary-total',
			summaryAddToCart: '.multiple-quick-buy__add-to-cart--summary',
			singleAddToCart: '.multiple-quick-buy__add-to-cart--single-product',
			quantityInput: 'input.quantity-input',
			hideSummaryInput: 'input[name="hide_summary"]',
			summaryContent: '.multiple-quick-buy__summary-content',
		};
	}

	connectedCallback() {
		// Cache elements first
		this.cacheElements();

		// Add event listeners with null checks
		this.addEventListeners();

		// Use requestAnimationFrame to ensure DOM is ready
		requestAnimationFrame(() => {
			this.updateSummary();
		});
	}

	cacheElements() {
		this.cachedElements = {
			summaryAddToCart: this.querySelector(this.selectors.summaryAddToCart),
			summaryQuantity: this.querySelector(this.selectors.summaryQuantity),
			summaryTotal: this.querySelector(this.selectors.summaryTotal),
			summary: this.querySelector(this.selectors.summary),
			summaryContent: this.querySelector(this.selectors.summaryContent),
			hideSummaryInput: this.querySelector(this.selectors.hideSummaryInput),
		};
	}

	addEventListeners() {
		// Add null checks before adding listeners
		Array.from(this.querySelectorAll(this.selectors.product)).forEach((product) => {
			const qtyInput = product.querySelector(this.selectors.quantityInput);
			if (qtyInput) {
				qtyInput.addEventListener('change', this.updateSummary.bind(this));
			}
		});

		if (this.cachedElements.summaryAddToCart) {
			this.cachedElements.summaryAddToCart.addEventListener('click', this.handleAddToCart.bind(this));
		}

		Array.from(this.querySelectorAll(this.selectors.singleAddToCart)).forEach((button) => {
			button.addEventListener('click', this.handleSingleAddToCart.bind(this));
		});
	}

	parsePrice(priceStr) {
		if (!priceStr) return 0;

		// Remove currency symbols and commas, parse float
		const num = parseFloat(priceStr.replace(/[^\d.,-]/g, '').replace(',', '.'));

		return isNaN(num) ? 0 : num;
	}

	updateSummary() {
		let totalQty = 0;
		let totalPrice = 0;
		let currency = '';
		let currencyAtStart = false;

		Array.from(this.querySelectorAll(this.selectors.product)).forEach((product, idx) => {
			const qtyInput = product.querySelector(this.selectors.quantityInput);
			const qty = qtyInput ? parseInt(qtyInput.value, 10) || 0 : 0;

			// Use data-price for accuracy
			const priceStr = product.getAttribute('data-price') || '';
			let price = 0;

			if (priceStr) {
				// Extract currency and numeric value
				const match = priceStr.match(/^(\D+)?([\d.,]+)(\s*\D+)?$/);

				if (match) {
					if (idx === 0) {
						if (match[1]) {
							currency = match[1].trim();
							currencyAtStart = true;
						} else if (match[3]) {
							currency = match[3].trim();
							currencyAtStart = false;
						}
					}
					price = parseFloat(match[2].replace(',', '.'));
				}
			}

			totalQty += qty;
			totalPrice += qty * price;
		});

		if (this.cachedElements.summaryQuantity) {
			this.cachedElements.summaryQuantity.textContent = totalQty;
		}
		if (this.cachedElements.summaryTotal) {
			let formattedTotal = '';

			if (currency) {
				const totalStr = totalPrice.toFixed(2);

				formattedTotal = currencyAtStart ? `${currency}${totalStr}` : `${totalStr} ${currency}`;
			} else {
				formattedTotal = totalPrice.toFixed(2);
			}
			this.cachedElements.summaryTotal.textContent = formattedTotal;
		}

		// Check for hide_summary input and hide summary content if present
		const hideSummaryInput = this.cachedElements.hideSummaryInput;
		const summaryContent = this.cachedElements.summaryContent;

		if (hideSummaryInput && summaryContent) {
			summaryContent.classList.add(this.classes.summaryHidden);
		}

		if (this.cachedElements.summary && totalQty > 0) {
			this.cachedElements.summary.classList.remove(this.classes.summaryHidden);
			this.cachedElements.summaryAddToCart.classList.remove(this.classes.summaryAddToCartDisabled);
		} else if (
			this.cachedElements.summary &&
			!this.cachedElements.summary.classList.contains(this.classes.summaryHidden)
		) {
			this.cachedElements.summary.classList.add(this.classes.summaryHidden);
			this.cachedElements.summaryAddToCart.classList.add(this.classes.summaryAddToCartDisabled);
		}
	}

	async handleAddToCart(e) {
		e.preventDefault();

		if (!this.cachedElements.summaryAddToCart) return;

		this.cachedElements.summaryAddToCart.classList.add(this.classes.summaryAddToCartLoading);
		this.cachedElements.summaryAddToCart.setAttribute('disabled', 'disabled');

		const items = [];

		Array.from(this.querySelectorAll(this.selectors.product)).forEach((product) => {
			const qtyInput = product.querySelector(this.selectors.quantityInput);
			const qty = qtyInput ? parseInt(qtyInput.value, 10) || 0 : 0;
			const variantId = product.getAttribute('data-variant-id');

			if (qty > 0 && variantId) {
				items.push({ id: variantId, quantity: qty });
			}
		});

		if (items.length === 0) {
			this.cachedElements.summaryAddToCart.classList.remove(this.classes.summaryAddToCartLoading);
			this.cachedElements.summaryAddToCart.removeAttribute('disabled');

			return;
		}

		try {
			const config = fetchConfig('javascript');

			config.headers['X-Requested-With'] = 'XMLHttpRequest';

			delete config.headers['Content-Type'];

			const formData = new FormData();

			items.forEach((item) => {
				formData.append('items[][id]', item.id);
				formData.append('items[][quantity]', item.quantity);
			});

			if (this.cartDrawer && typeof this.cartDrawer.getSectionsToRender === 'function') {
				formData.append(
					'sections',
					this.cartDrawer.getSectionsToRender().map((section) => section.section)
				);
			} else {
				formData.append('sections', getCartBubbleSectionNames().join(','));
			}
			formData.append('sections_url', window.location.pathname);

			config.body = formData;

			const res = await fetch(`${window.routes.cart_add_url}.js`, config);
			const parsedState = await res.json();

			if (this.cartDrawer && typeof this.cartDrawer.renderContents === 'function') {
				this.cartDrawer.renderContents(parsedState);
			} else {
				updateCartBubbles(parsedState);
			}

			const cartUpdatedEvent = new CustomEvent('cart:updated', { detail: parsedState });
			const imagesReloadEvent = new CustomEvent('lazy-image:reload', { bubbles: true, detail: this });
			const cartDrawerLoadEvent = new CustomEvent('cart-drawer:load', { bubbles: true, detail: this });

			document.dispatchEvent(imagesReloadEvent);
			document.dispatchEvent(cartUpdatedEvent);
			document.dispatchEvent(cartDrawerLoadEvent);

			if (this.drawer && typeof this.drawer.openDrawer === 'function') {
				this.drawer.openDrawer('cart');
			}
		} catch (err) {
			console.error('MultipleQuickBuy add to cart error', err);

			this.dispatchEvent(new CustomEvent('multiple-quick-buy:error', { detail: err, bubbles: true }));
		} finally {
			this.cachedElements.summaryAddToCart.classList.remove(this.classes.summaryAddToCartLoading);
			this.cachedElements.summaryAddToCart.removeAttribute('disabled');

			Array.from(this.querySelectorAll(this.selectors.product)).forEach((product) => {
				const qtyInput = product.querySelector(this.selectors.quantityInput);

				if (qtyInput) {
					qtyInput.value = 0;
				}
			});

			this.updateSummary();
		}
	}

	async handleSingleAddToCart(e) {
		e.preventDefault();

		const button = e.currentTarget;
		const product = button.closest(this.selectors.product);

		if (!product || button.classList.contains(this.classes.singleAddToCartLoading)) return;

		button.classList.add(this.classes.singleAddToCartLoading);
		button.setAttribute('disabled', 'disabled');

		const qtyInput = product.querySelector(this.selectors.quantityInput);
		const qty = qtyInput ? parseInt(qtyInput.value, 10) || 0 : 1; // Default to 1 if no quantity input
		const variantId = product.getAttribute('data-variant-id');

		if (qty <= 0 || !variantId) {
			button.classList.remove(this.classes.singleAddToCartLoading);
			button.removeAttribute('disabled');

			return;
		}

		try {
			const config = fetchConfig('javascript');

			config.headers['X-Requested-With'] = 'XMLHttpRequest';

			delete config.headers['Content-Type'];

			const formData = new FormData();

			formData.append('items[][id]', variantId);
			formData.append('items[][quantity]', qty);

			if (this.cartDrawer && typeof this.cartDrawer.getSectionsToRender === 'function') {
				formData.append(
					'sections',
					this.cartDrawer.getSectionsToRender().map((section) => section.section)
				);
			} else {
				formData.append('sections', getCartBubbleSectionNames().join(','));
			}
			formData.append('sections_url', window.location.pathname);

			config.body = formData;

			const res = await fetch(`${window.routes.cart_add_url}.js`, config);
			const parsedState = await res.json();

			if (this.cartDrawer && typeof this.cartDrawer.renderContents === 'function') {
				this.cartDrawer.renderContents(parsedState);
			} else {
				updateCartBubbles(parsedState);
			}

			const cartUpdatedEvent = new CustomEvent('cart:updated', { detail: parsedState });
			const imagesReloadEvent = new CustomEvent('lazy-image:reload', { bubbles: true, detail: this });
			const cartDrawerLoadEvent = new CustomEvent('cart-drawer:load', { bubbles: true, detail: this });

			document.dispatchEvent(imagesReloadEvent);
			document.dispatchEvent(cartUpdatedEvent);
			document.dispatchEvent(cartDrawerLoadEvent);

			if (this.drawer && typeof this.drawer.openDrawer === 'function') {
				this.drawer.openDrawer('cart');
			}

			// Reset quantity for this specific product only if quantity input exists
			if (qtyInput) {
				qtyInput.value = 0;
			}

			this.updateSummary();
		} catch (err) {
			console.error('MultipleQuickBuy single add to cart error', err);

			this.dispatchEvent(new CustomEvent('multiple-quick-buy:error', { detail: err, bubbles: true }));
		} finally {
			button.classList.remove(this.classes.singleAddToCartLoading);
			button.removeAttribute('disabled');
		}
	}
}

if (!customElements.get('multiple-quick-buy')) {
	customElements.define('multiple-quick-buy', MultipleQuickBuy);
}
